import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://102.220.100.99/api';

const ServiceProviders = () => {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [adapterTypes, setAdapterTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    api_type: '',
    api_endpoint: '',
    api_key: '',
    api_username: '',
    api_password: '',
    api_headers: {},
    api_config: {},
    field_mapping: {},
    active: true
  });

  // Default configurations for different adapter types
  const getDefaultConfig = (adapterType) => {
    const configs = {
      miway: {
        method: 'POST',
        contentType: 'application/json',
        endpoint: 'https://api.miway.co.za/leads',
        requiredFields: ['firstName', 'lastName', 'email', 'phone', 'idNumber'],
        fieldMapping: {
          firstName: 'customer_first_name',
          lastName: 'customer_last_name',
          email: 'customer_email',
          phone: 'customer_phone',
          idNumber: 'customer_id_number'
        }
      },
      returnx: {
        method: 'POST',
        contentType: 'application/x-www-form-urlencoded',
        endpoint: 'https://returnxdigital.leadbyte.co.uk/api/submit.php',
        requiredFields: ['firstName', 'lastName', 'email', 'phone'],
        fieldMapping: {
          firstName: 'first_name',
          lastName: 'last_name',
          email: 'email',
          phone: 'phone_number'
        }
      },
      vicidial: {
        method: 'GET',
        contentType: 'application/x-www-form-urlencoded',
        endpoint: 'http://102.220.100.175/vicidial/non_agent_api.php',
        requiredFields: ['firstName', 'lastName', 'phone'],
        fieldMapping: {
          firstName: 'first_name',
          lastName: 'last_name',
          phone: 'phone_number',
          email: 'email',
          address1: 'address1',
          address2: 'address2',
          address3: 'address3',
          city: 'city',
          state: 'state',
          province: 'province',
          postalCode: 'postal_code',
          country: 'country_code',
          altPhone: 'alt_phone',
          dateOfBirth: 'date_of_birth',
          gender: 'gender',
          comments: 'comments',
          vendorLeadCode: 'vendor_lead_code',
          sourceId: 'source_id',
          gmt: 'gmt_offset_now',
          title: 'title',
          middleInitial: 'middle_initial',
          security: 'security_phrase',
          rank: 'rank',
          ownerPopulate: 'owner_populate'
        },
        staticParams: {
          source: 'qualitron',
          user: '6665',
          pass: '1234',
          function: 'add_lead',
          phone_code: '1',
          list_id: '999'
        }
      },
      universal: {
        method: 'POST',
        contentType: 'application/json',
        endpoint: '',
        requiredFields: [],
        fieldMapping: {}
      }
    };
    return configs[adapterType] || configs.universal;
  };

  useEffect(() => {
    fetchProviders();
    fetchAdapterTypes();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_URL}/service-providers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch providers');
      }

      setProviders(data.data || data);
    } catch (err) {
      console.error('Fetch providers error:', err);
      setError(err.message || 'Failed to fetch providers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdapterTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/service-providers/adapter-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch adapter types');
      }

      setAdapterTypes(data.data || []);
    } catch (err) {
      console.error('Fetch adapter types error:', err);
    }
  };

  const handleOpenDialog = (provider = null) => {
    if (provider) {
      setFormData({
        name: provider.name || '',
        slug: provider.slug || '',
        description: provider.description || '',
        api_type: provider.api_type || '',
        api_endpoint: provider.api_endpoint || '',
        api_key: provider.api_key || '',
        api_username: provider.api_username || '',
        api_password: provider.api_password || '',
        api_headers: provider.api_headers || {},
        api_config: provider.api_config || {},
        field_mapping: provider.field_mapping || {},
        active: provider.active !== false
      });
      setSelectedProvider(provider);
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        api_type: '',
        api_endpoint: '',
        api_key: '',
        api_username: '',
        api_password: '',
        api_headers: {},
        api_config: {},
        field_mapping: {},
        active: true
      });
      setSelectedProvider(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProvider(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      api_type: '',
      api_endpoint: '',
      api_key: '',
      api_username: '',
      api_password: '',
      api_headers: {},
      api_config: {},
      field_mapping: {},
      active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAdapterTypeChange = (adapterType) => {
    const defaultConfig = getDefaultConfig(adapterType);
    setFormData(prev => ({
      ...prev,
      api_type: adapterType,
      api_endpoint: defaultConfig.endpoint,
      api_config: defaultConfig,
      field_mapping: defaultConfig.fieldMapping
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = selectedProvider 
        ? `${API_URL}/service-providers/${selectedProvider.id}`
        : `${API_URL}/service-providers`;
      
      const method = selectedProvider ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save provider');
      }

      setError('');
      handleCloseDialog();
      fetchProviders();
    } catch (err) {
      setError(err.message || 'Failed to save provider');
    }
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(`Are you sure you want to delete "${provider.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/service-providers/${provider.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete provider');
      }

      fetchProviders();
    } catch (err) {
      setError(err.message || 'Failed to delete provider');
    }
  };

  const testConnection = async (provider) => {
    try {
      const response = await fetch(`${API_URL}/service-providers/${provider.id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        alert('Connection test successful!');
      } else {
        alert(`Connection test failed: ${data.error}`);
      }
    } catch (err) {
      alert(`Connection test failed: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading providers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Service Providers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Provider
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {providers.map((provider) => (
          <Grid item xs={12} md={6} lg={4} key={provider.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    {provider.name}
                  </Typography>
                  <Chip 
                    label={provider.active ? 'Active' : 'Inactive'} 
                    color={provider.active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Typography color="text.secondary" gutterBottom>
                  {provider.description || 'No description'}
                </Typography>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Type:</strong> {provider.api_type}
                </Typography>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Endpoint:</strong> {provider.api_endpoint}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title="Test Connection">
                    <IconButton 
                      size="small" 
                      onClick={() => testConnection(provider)}
                      color="primary"
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Edit Provider">
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(provider)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete Provider">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(provider)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {providers.length === 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <InfoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Service Providers Configured
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Set up API integrations with external providers to automatically submit leads from your call analysis.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Your First Provider
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Provider Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedProvider ? 'Edit Provider' : 'Add New Provider'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Provider Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  helperText="Unique identifier (lowercase, no spaces)"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Adapter Type</InputLabel>
                  <Select
                    value={formData.api_type}
                    onChange={(e) => handleAdapterTypeChange(e.target.value)}
                    label="Adapter Type"
                  >
                    {adapterTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={handleInputChange}
                      name="active"
                    />
                  }
                  label="Active"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Endpoint"
                  name="api_endpoint"
                  value={formData.api_endpoint}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Key"
                  name="api_key"
                  type="password"
                  value={formData.api_key}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Username"
                  name="api_username"
                  value={formData.api_username}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Password"
                  name="api_password"
                  type="password"
                  value={formData.api_password}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>

            {/* Advanced Configuration */}
            <Accordion sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Advanced Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* API Headers Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      API Headers
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Additional headers to send with API requests
                    </Typography>
                    
                    {Object.entries(formData.api_headers || {}).map(([key, value], index) => (
                      <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Header Name"
                            value={key}
                            onChange={(e) => {
                              const newHeaders = { ...formData.api_headers };
                              delete newHeaders[key];
                              newHeaders[e.target.value] = value;
                              setFormData(prev => ({ ...prev, api_headers: newHeaders }));
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Header Value"
                            value={value}
                            onChange={(e) => {
                              const newHeaders = { ...formData.api_headers };
                              newHeaders[key] = e.target.value;
                              setFormData(prev => ({ ...prev, api_headers: newHeaders }));
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            onClick={() => {
                              const newHeaders = { ...formData.api_headers };
                              delete newHeaders[key];
                              setFormData(prev => ({ ...prev, api_headers: newHeaders }));
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newHeaders = { ...formData.api_headers };
                        newHeaders[`header_${Object.keys(newHeaders).length + 1}`] = '';
                        setFormData(prev => ({ ...prev, api_headers: newHeaders }));
                      }}
                      variant="outlined"
                      size="small"
                    >
                      Add Header
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Field Mapping Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Field Mapping
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Map internal field names to provider-specific field names
                    </Typography>
                    
                    {Object.entries(formData.field_mapping || {}).map(([internalField, providerField], index) => (
                      <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Internal Field"
                            value={internalField}
                            onChange={(e) => {
                              const newMapping = { ...formData.field_mapping };
                              delete newMapping[internalField];
                              newMapping[e.target.value] = providerField;
                              setFormData(prev => ({ ...prev, field_mapping: newMapping }));
                            }}
                            size="small"
                            helperText="e.g., firstName, lastName, email"
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Provider Field"
                            value={providerField}
                            onChange={(e) => {
                              const newMapping = { ...formData.field_mapping };
                              newMapping[internalField] = e.target.value;
                              setFormData(prev => ({ ...prev, field_mapping: newMapping }));
                            }}
                            size="small"
                            helperText="e.g., first_name, customer_name"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            onClick={() => {
                              const newMapping = { ...formData.field_mapping };
                              delete newMapping[internalField];
                              setFormData(prev => ({ ...prev, field_mapping: newMapping }));
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newMapping = { ...formData.field_mapping };
                        newMapping[`field_${Object.keys(newMapping).length + 1}`] = '';
                        setFormData(prev => ({ ...prev, field_mapping: newMapping }));
                      }}
                      variant="outlined"
                      size="small"
                    >
                      Add Field Mapping
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Common Field Mapping Presets */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Quick Presets
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            field_mapping: {
                              firstName: 'first_name',
                              lastName: 'last_name',
                              email: 'email',
                              phone: 'phone_number',
                              idNumber: 'id_number'
                            }
                          }));
                        }}
                      >
                        Standard Fields
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            field_mapping: {
                              firstName: 'first_name',
                              lastName: 'last_name',
                              phone: 'phone_number',
                              email: 'email',
                              address1: 'address1',
                              address2: 'address2',
                              city: 'city',
                              state: 'state',
                              postalCode: 'postal_code',
                              country: 'country_code'
                            }
                          }));
                        }}
                      >
                        Vicidial Basic
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            field_mapping: {
                              firstName: 'first_name',
                              lastName: 'last_name',
                              phone: 'phone_number',
                              email: 'email',
                              address1: 'address1',
                              address2: 'address2',
                              address3: 'address3',
                              city: 'city',
                              state: 'state',
                              province: 'province',
                              postalCode: 'postal_code',
                              country: 'country_code',
                              altPhone: 'alt_phone',
                              dateOfBirth: 'date_of_birth',
                              gender: 'gender',
                              comments: 'comments',
                              title: 'title',
                              middleInitial: 'middle_initial'
                            }
                          }));
                        }}
                      >
                        Vicidial Complete
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            api_headers: {
                              'Content-Type': 'application/json',
                              'Accept': 'application/json',
                              'User-Agent': 'Qualitron-API/1.0'
                            }
                          }));
                        }}
                      >
                        Standard Headers
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedProvider ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ServiceProviders; 